/* /system-owner-report/:sspId — builds self-contained report HTML for the System Owner.
   Streams /data/stream (SSE, progressive sections + progress) and
   /data/executive-summary in parallel. Download is disabled until both fully
   resolve so partial data is never exported. */
import { Download, FileArchive, User, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  fetchEvidenceBase64,
  fetchReportExecutiveSummary,
  streamReport,
  type ReportProgress,
} from "./services/reportApi";
import { useToastStore } from "../../../stores/toastStore";
import { PageShell } from "../../components/layout/PageShell";
import { Button } from "../../components/ui/button";
import { RegoWaterShield } from "./components/ReportWaterLoader";
import { mapReportData } from "./utils/mapReportData";
import type { ReportPayload } from "./types/reportTypes";
import { downloadReport } from "./services/downloadReport";
import {
  buildAuditReportHtml,
  type ExecSummaryState,
} from "./utils/buildAuditReportHtml";

export function SystemOwnerReportDetail() {
  const { sspId } = useParams<{ sspId: string }>();
  const navigate = useNavigate();

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [progress, setProgress] = useState<ReportProgress>({
    percent: 0,
    phase: "Starting...",
  });
  const [streamDone, setStreamDone] = useState(false);
  const [execState, setExecState] = useState<ExecSummaryState>("loading");
  const [execText, setExecText] = useState("");
  const [downloading, setDownloading] = useState<"html" | "evidence" | null>(
    null,
  );
  // Accumulates each `section` event's payload (shallow + nested `report` merge).
  const rawRef = useRef<Record<string, any>>({});
  const hasAnnouncedLoaded = useRef(false);
  const mergeRaw = (chunk: unknown) => {
    const prev = rawRef.current;
    const c = (chunk ?? {}) as Record<string, any>;
    rawRef.current = {
      ...prev,
      ...c,
      report: { ...(prev.report ?? {}), ...(c.report ?? {}) },
    };
  };

  // Streams the main payload + fetches the exec summary in parallel; resets on sspId change.
  useEffect(() => {
    let alive = true;
    if (!sspId) {
      navigate("/report");
      return;
    }

    setPayload(null);
    setProgress({ percent: 0, phase: "Starting..." });
    setStreamDone(false);
    setExecState("loading");
    setExecText("");
    rawRef.current = {};
    hasAnnouncedLoaded.current = false;

    const ctrl = new AbortController();

    const onProgress = (p: ReportProgress) => {
      if (alive) setProgress(p);
    };
    const onSection = (_name: string, sectionPayload: unknown) => {
      if (!alive) return;
      mergeRaw(sectionPayload);
      setPayload(mapReportData(rawRef.current, sspId));
    };

    streamReport(sspId, onProgress, ctrl.signal, onSection)
      .then((raw) => {
        if (!alive) return;
        mergeRaw(raw);
        setPayload(mapReportData(rawRef.current, sspId));
        setProgress({ percent: 100, phase: "Complete" });
        setStreamDone(true);
      })
      .catch((err) => {
        if (!alive || err?.name === "AbortError") return;
        useToastStore
          .getState()
          .showError(
            "Report unavailable",
            "Could not load the report. Please try again.",
          );
        navigate("/report");
      });

    fetchReportExecutiveSummary(sspId)
      .then((text) => {
        if (!alive) return;
        setExecText(text || "");
        setExecState("ready");
      })
      .catch(() => {
        if (!alive) return;
        setExecState("error");
      });

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [sspId, navigate]);

  // The iframe has no auth token, so it asks us to fetch evidence files and posts the bytes back.
  useEffect(() => {
    const onMsg = async (e: MessageEvent) => {
      // Validate that message originates from the same origin or internal srcdoc iframe
      if (e.origin !== window.location.origin && e.origin !== "null") return;
      const d = e.data as { type?: string; resourceId?: string } | null;
      if (!d || d.type !== "rego-evidence-request" || !d.resourceId) return;
      const src = e.source as Window | null;
      if (!src) return;
      const rid = d.resourceId;

      let payload: { type: string; resourceId: string; fileName?: string; contentType?: string; base64?: string };
      try {
        const r = await fetchEvidenceBase64(rid);
        payload = { type: "rego-evidence-result", resourceId: rid, ...r };
      } catch {
        payload = { type: "rego-evidence-result", resourceId: rid };
      }
      src.postMessage(payload, window.location.origin);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const pct = Math.min(100, Math.max(0, Math.round(progress.percent)));

  const fullyReady = streamDone && execState !== "loading" && pct >= 100;
  const ready = fullyReady && !!payload;

  useEffect(() => {
    if (fullyReady && !hasAnnouncedLoaded.current) {
      hasAnnouncedLoaded.current = true;
      useToastStore.getState().showSuccess("Success", "Report loaded");
    }
  }, [fullyReady]);

  const html = useMemo(
    () =>
      ready && payload
        ? buildAuditReportHtml(payload, {
          execSummary: { state: execState, text: execText },
          audience: "system_owner",
        })
        : "",
    [ready, payload, execState, execText],
  );

  const handleDownload = async (withEvidence: boolean) => {
    if (downloading || !payload || !fullyReady) return;
    setDownloading(withEvidence ? "evidence" : "html");
    try {
      const res = await downloadReport(payload, {
        execState,
        execText,
        withEvidence,
        audience: "system_owner",
      });
      if (res.evidenceTotal > 0 && res.evidenceFetched < res.evidenceTotal) {
        useToastStore
          .getState()
          .showError(
            "Evidence",
            `Only ${res.evidenceFetched} of ${res.evidenceTotal} evidence files could be fetched.`,
          );
      }
    } catch {
      useToastStore
        .getState()
        .showError("Download", "Could not generate the report download.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <PageShell className="flex flex-col h-[calc(100vh-7rem)] space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/report")}
          className="size-auto flex-shrink-0 p-1.5 rounded-md text-slate-400 hover:text-foreground hover:bg-accent"
          aria-label="Close report"
          title="Close report"
        >
          <X className="size-5" />
        </Button>
        <div className="flex items-center gap-3">
          {ready && !fullyReady && (
            <div
              className="flex items-center gap-2 text-xs text-slate-500"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span className="relative flex h-1.5 w-24 overflow-hidden rounded-full bg-slate-200">
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-indigo-500 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </span>
              <span className="tabular-nums">
                {progress.phase} {pct}%
              </span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-2 text-[11px] font-bold text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300"
              title="System Owner Report"
            >
              <User className="size-3.5" />
              System Owner
            </span>
            <Button
              onClick={() => handleDownload(false)}
              disabled={!fullyReady || !!downloading}
              className="h-auto px-4 py-2 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              title="Download the System Owner report"
            >
              <Download className="size-3.5" />
              {downloading === "html" ? "Preparing..." : "Download Report"}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownload(true)}
              disabled={!fullyReady || !!downloading}
              className="h-auto px-4 py-2 text-xs font-bold border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50"
              title="Download the zip with evidence embedded (System Owner)"
            >
              <FileArchive className="size-3.5" />
              {downloading === "evidence" ? "Preparing..." : "Download with Evidence"}
            </Button>
          </div>
        </div>
      </div>
      {ready && payload ? (
        <iframe
          title={`System Owner Report - ${payload.meta.sspName}`}
          srcDoc={html}
          className="flex-1 w-full rounded-lg border border-slate-200 bg-white"
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-5 rounded-lg border border-slate-200 bg-white">
          <div
            className="relative w-32 h-32"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <RegoWaterShield pct={pct} />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-600">
              {progress.phase}{" "}
              <span className="font-semibold tabular-nums text-indigo-600">
                {pct}%
              </span>
            </p>
            <p className="mt-1 text-xs text-slate-400">Generating report...</p>
          </div>
        </div>
      )}
    </PageShell>
  );
}
